/**
 * /apps 폰 화면 (≤768px) — spec docs/superpowers/specs/2026-09-24-apps-mobile-design.md.
 *
 * 두 보기를 #appsContainer[data-m-view] 로 오간다: 런처(사내 서비스 격자 — 서비스마다 한 칸, 열린 앱은
 * 그 칸에 점 · × 로 표시한다)와 전체 화면
 * 앱(얇은 줄 ‹ · 이름 ▾ · ⟳ · ⋯, [data-m-app] 이 보이는 앱). 프레임·이동 이력·브리지·저장은
 * apps.js 가 그대로 갖고, 이 모듈은 apps.js 가 넘긴 API 와 #appsContainer 의 apps:changed 만 쓴다.
 *
 * 뒤로가기 (계획 R1 + 최종 리뷰 I1–I5): 보이는 화면이 바뀔 때마다 최상위 이력에 칸을 쌓는다 —
 * 런처에서 앱으로 들어갈 때, 그리고 앱을 전환할 때(시트·브리지) {appsView:'app', appsId, appsSeq}.
 * 프레임 안의 이동은 브라우저가 같은 이력 줄(joint session history)에 쌓으므로, 전환에 칸을 쌓지
 * 않으면 뒤로가기가 숨은 앱을 몰래 되감는다(I1). 그래서 뒤로가기는 언제나 "직전에 보던 것" 이다:
 * 보이는 앱의 이전 페이지, 또는 직전 앱(그 페이지 그대로), 또는 런처. ‹ 는 "위로(런처)" 이고
 * leaveMode 가 걷어 낼지(pop)·제자리 교체할지(replace)·런처 칸을 쌓을지(push) 정한다(되감으면 앱이
 * 보던 페이지를 잃는다; 이력 길이 상한에서는 늘 push). popDecision 은 보이는 것을 바꾸지 않을 칸 —
 * 닫힌 앱의 칸, 런처를 보는 중의 런처 칸 — 을 같은 방향으로 건너뛴다(상태 없이, I2). 새로고침은
 * 보던 화면을 지킨다 (2026-09-30, 사용자 「새로고침하면 앱이 닫혀 버린다」): 지금 칸이 되살린 앱의 칸이면
 * 그 앱을 그 페이지 그대로 보이고, 아니면(앱이 닫혔거나 셋 밖) 런처 칸으로 바꿔 둔다(R2 — 그대로 두면 다음
 * 뒤로가기가 그 칸을 앱으로 읽어 화면이 바뀌지 않는다). ?open= 칸은 앱 칸으로 표시한다(I4).
 * 데스크톱 폭에서는 폰이 쌓은 칸이 보이는 것을 바꾸지 않으므로 같은 방향으로 계속 간다(I5).
 *
 * 살아 있는 앱은 셋까지 (apps-mobile-logic.js, 최근 순서 localStorage portalAppsRecent).
 * 데스크톱 폭에서는 아무것도 하지 않는다 — 처리기는 모두 isPhone() 을 먼저 본다.
 */
import { openExclusive, closedExclusive } from './popover-registry.js';
import { registerSheetHooks, closeSheet } from './mobile-shell.js';
import { showNotification } from './notifications.js';
import { PHONE_LIVE_MAX, recentOrder, touch, evictions, restoreSet, leaveMode, popDecision, fillName, switchList } from './apps-mobile-logic.js';
import { phoneQuery } from './phone.js';

const PHONE = phoneQuery;
const RECENT_KEY = 'portalAppsRecent';

export function isPhone() {
    return PHONE.matches;
}

function loadRecent() {
    try {
        const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
    } catch (e) {
        return [];
    }
}

function saveRecent(order) {
    try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(order));
    } catch (e) {
        /* 무해 */
    }
}

// apps.js 의 복원이 폰에서 되살릴 앱: 저장된 열린 앱 중 가장 최근 셋 (저장된 순서 그대로).
export function phoneRestoreIds(savedOpen, active) {
    return restoreSet(savedOpen, loadRecent(), active, PHONE_LIVE_MAX);
}

function el(tag, cls, attrs, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    Object.entries(attrs || {}).forEach(([k, v]) => n.setAttribute(k, v));
    if (text != null) n.textContent = text;
    return n;
}

export function setupAppsMobile(api) {
    const container = document.getElementById('appsContainer');
    const bar = document.getElementById('appMobileBar');
    const launcher = document.getElementById('appLauncher');
    if (!api || !container || !bar || !launcher || container.dataset.appsMobileBound) return;
    container.dataset.appsMobileBound = '1';
    const nameBtn = document.getElementById('appMobileName');
    const nameText = document.getElementById('appMobileNameText');
    const nameIcon = document.getElementById('appMobileIcon');
    const menu = document.getElementById('appMobileMenu');
    const moreBtn = document.getElementById('appMobileMore');
    const sheet = document.getElementById('appSwitchSheet');
    const sheetAll = document.getElementById('appSwitchAll');

    // 새로고침: 지금 칸이 되살린 앱의 칸이면 그 앱을 그대로 보인다(아래 「시작」). 되살리지 못한 앱의 칸이면
    // (R2) 런처 칸으로 바꿔 둔다 — 그대로 두면 다음 뒤로가기가 이 칸을 앱으로 읽어(popDecision) 화면이
    // 바뀌지 않는다. 순서(appsSeq)는 지킨다.
    const reloadedApp = history.state && history.state.appsView === 'app' && history.state.appsId && api.openIds().includes(history.state.appsId)
        ? history.state.appsId
        : '';
    if (history.state && history.state.appsView === 'app' && !reloadedApp) {
        history.replaceState(Object.assign({}, history.state, { appsView: 'launcher', appsId: undefined }), '', location.href);
    }

    let order = recentOrder(api.openIds(), loadRecent()); // 오래된 것 → 최근 것
    let busy = false; // 우리가 부른 api 가 올리는 apps:changed 는 끝난 뒤 한 번에 그린다
    const baseLength = history.length;
    let mine = null; // 우리가 쌓은 앱 칸 {length, seq}
    let seq = (history.state && history.state.appsSeq) || 0;
    let ownPopAt = 0; // ‹ 가 'pop' 으로 부른 history.back() 의 시각 — 그 popstate 는 건너뛰기 판단에서 뺀다

    const view = () => container.dataset.mView || 'launcher';
    const shown = () => (view() === 'app' ? container.dataset.mApp || '' : '');
    const nextSeq = () => (seq = Math.max(Date.now(), seq + 1));
    const closeLabel = (name) => fillName(bar.dataset.closeNamed || '{name} 닫기', name);
    const switchLabel = (nameBtn && nameBtn.getAttribute('title')) || '앱 전환';
    let lastSig = ''; // 마지막으로 그린 목록의 지문 — 이동만 있을 때는 목록을 다시 만들지 않는다 (Minor 2)
    const sigOf = () => api.openIds().join(',') + '|' + shown();
    const sheetOpenNow = () => !!sheet && !sheet.hidden;

    function quietly(fn) {
        busy = true;
        try {
            fn();
        } finally {
            busy = false;
        }
    }

    function iconOf(id) {
        const card = launcher.querySelector('.app-ntp-card[data-app-id="' + CSS.escape(id) + '"]');
        const src = card && card.querySelector('.app-ntp-icon');
        return src ? src.cloneNode(true) : el('span', 'app-ntp-icon');
    }

    // ── 최근 순서 · 셋 상한 ──
    function remember(id) {
        order = touch(recentOrder(api.openIds(), order), id);
        saveRecent(order);
    }
    function settleOrder() {
        order = recentOrder(api.openIds(), order);
        saveRecent(order);
    }
    // 살아 있는 앱이 셋을 넘으면 가장 오래 보지 않은 앱부터 닫는다 (지금 보이는 앱은 빼고).
    function enforceCap(keep) {
        quietly(() => {
            order = recentOrder(api.openIds(), order);
            evictions(order, keep, PHONE_LIVE_MAX).forEach((x) => api.close(x));
        });
        settleOrder();
    }

    // ── 그리기 ──
    // 시트의 한 줄: 열린 앱이면 점(data-open)과 × 가 붙는다 — 서비스마다 한 줄 (2026-09-29).
    function sheetRow(id, cur, closable) {
        const info = api.appInfo(id) || { name: id, mode: 'iframe' };
        const li = el('li', 'app-m-row', { 'data-app-id': id });
        if (closable) li.setAttribute('data-open', '');
        const pick = el('button', 'm-sheet-item app-m-row-open', { type: 'button', 'data-appm-open': id });
        if (id === cur) pick.setAttribute('aria-current', 'true');
        const end = id === cur ? '✓' : info.mode === 'tab' ? '↗' : '';
        pick.append(iconOf(id), el('span', 'app-m-row-name', {}, info.name), el('span', 'app-m-row-end', { 'aria-hidden': 'true' }, end));
        li.append(pick);
        if (closable) li.append(el('button', 'app-m-row-close', { type: 'button', 'data-appm-close': id, 'aria-label': closeLabel(info.name) }, '×'));
        return li;
    }
    function fillSheet() {
        const cur = shown();
        const openIds = api.openIds();
        const open = new Set(openIds);
        const all = Array.from(launcher.querySelectorAll('.app-ntp-card[data-app-id]')).map((c) => c.dataset.appId);
        sheetAll.replaceChildren(...switchList(all, recentOrder(openIds, order)).map((x) => sheetRow(x, cur, open.has(x))));
    }
    function renderBar() {
        const id = shown();
        const info = id ? api.appInfo(id) : null;
        nameText.textContent = info ? info.name : '';
        // 보이는 글자는 앱 이름뿐이다 — 화면 읽기에는 이 단추가 앱을 바꾼다는 것도 알린다 (Minor 3)
        if (nameBtn) nameBtn.setAttribute('aria-label', info ? info.name + ' — ' + switchLabel : switchLabel);
        nameIcon.replaceChildren(...(id ? [iconOf(id)] : []));
        const back = menu.querySelector('[data-appm="back"]');
        const fwd = menu.querySelector('[data-appm="forward"]');
        if (back) back.disabled = !api.canBack();
        if (fwd) fwd.disabled = !api.canForward();
    }
    // 런처 격자의 칸마다 열림 표시(점 · 숨은 「열림」 · ×)를 맞춘다 — 칸을 다시 만들지 않는다.
    function render() {
        lastSig = sigOf();
        const open = new Set(api.openIds());
        launcher.querySelectorAll('.app-ntp-cell[data-app-id]').forEach((cell) => {
            const on = open.has(cell.dataset.appId);
            cell.toggleAttribute('data-open', on);
            cell.querySelectorAll('.app-ntp-close, .app-ntp-state').forEach((n) => { n.hidden = !on; });
        });
        if (view() === 'app') renderBar();
    }

    // ── ⋯ 메뉴 (공용 팝오버 등록부: 바깥 클릭·Esc·다른 팝오버와 배타) ──
    function closeMenu(restoreFocus) {
        if (!menu || menu.hidden) return;
        // 포커스가 메뉴 안에 있었으면(Esc·항목 누름) ⋯ 로 돌려준다 — 숨은 메뉴에 남으면 body 로 떨어진다 (Minor 1).
        // 바깥을 눌러 닫혔으면 포커스는 이미 그곳에 있다.
        const inside = menu.contains(document.activeElement);
        menu.hidden = true;
        moreBtn.setAttribute('aria-expanded', 'false');
        closedExclusive('apps-mobile-menu');
        if (restoreFocus || inside) moreBtn.focus({ preventScroll: true });
    }
    function openMenu() {
        renderBar(); // ←/→ 비활성 최신화
        menu.hidden = false;
        moreBtn.setAttribute('aria-expanded', 'true');
        openExclusive('apps-mobile-menu', () => closeMenu(), { roots: () => [menu, moreBtn] });
        const first = menu.querySelector('button:not([disabled])');
        if (first) first.focus({ preventScroll: true });
    }

    // ── 포커스 (Minor 3): 사용자가 보기를 바꾸면 새 보기의 첫 자리로, popstate 로 바뀌었으면 숨은 요소에
    //    남은 포커스만 옮긴다 (아무것도 포커스하지 않은 터치 사용자를 건드리지 않는다).
    function launcherFocusTarget(leftId) {
        return (leftId && launcher.querySelector('.app-ntp-card[data-app-id="' + CSS.escape(leftId) + '"]'))
            || launcher.querySelector('.app-ntp-card');
    }
    function focusTo(el) {
        if (el) el.focus({ preventScroll: true });
    }
    function settleFocus(el) {
        const a = document.activeElement;
        if (a && a !== document.body && (!a.isConnected || a.getClientRects().length === 0)) focusTo(el);
    }

    // ── 보기 ──
    function showApp(id) {
        quietly(() => api.activate(id));
        container.dataset.mView = 'app';
        container.dataset.mApp = id;
        remember(id);
        enforceCap(id);
        render();
    }
    function showLauncher() {
        closeMenu();
        container.dataset.mView = 'launcher';
        delete container.dataset.mApp;
        render();
    }
    function pushApp(id) {
        history.pushState({ appsView: 'app', appsId: id, appsSeq: nextSeq() }, '', location.href);
    }
    // 다른 앱으로 옮긴다 (시트·브리지 전환): 칸 하나를 쌓는다 — 뒤로가기가 직전 앱을 그 페이지 그대로
    // 되돌려 보인다 (리뷰 I1). 이 칸은 ‹ 가 걷어 낼 칸이 아니다(걷어 내면 직전 앱이 보인다) — mine 을 비운다.
    function followApp(id) {
        showApp(id);
        pushApp(id);
        mine = null;
    }
    // 런처 격자에서 앱으로: 새 탭 앱은 새 창만 열고 보기는 그대로, 나머지는 전체 화면 + 앱 칸 하나.
    function enter(id) {
        const info = api.appInfo(id);
        if (!info) return;
        if (info.mode === 'tab') {
            api.open(id);
            return;
        }
        quietly(() => api.open(id));
        showApp(id);
        pushApp(id);
        mine = { length: history.length, seq };
        focusTo(nameBtn);
    }
    function switchTo(id) {
        const info = api.appInfo(id);
        if (!info) return;
        if (info.mode === 'tab') {
            api.open(id);
            return;
        }
        if (view() !== 'app') {
            enter(id);
            return;
        }
        if (id === shown()) return; // 지금 앱을 고르면 시트만 닫힌다 (spec §1.3)
        quietly(() => api.open(id));
        followApp(id);
    }
    // ‹ 와 「이 앱 닫기」: 런처로. 이력은 leaveMode 가 정한다 (머리말 R1).
    function toLauncher() {
        const mode = leaveMode({ length: history.length, state: history.state, mine, baseLength });
        const url = new URL(location.href);
        url.searchParams.delete('open');
        const clean = url.pathname + url.search + url.hash;
        mine = null;
        const left = shown();
        showLauncher();
        focusTo(launcherFocusTarget(left));
        if (mode === 'pop') {
            ownPopAt = Date.now();
            history.back();
        }
        else if (mode === 'replace') history.replaceState({ appsView: 'launcher', appsSeq: seq }, '', clean);
        else history.pushState({ appsView: 'launcher', appsSeq: nextSeq() }, '', clean);
    }
    // × (런처 칸·시트) 와 「이 앱 닫기」: 보이는 앱이면 먼저 런처로 나간다.
    function closeApp(id) {
        if (shown() === id) {
            if (sheetOpenNow()) closeSheet(sheet);
            toLauncher();
        }
        quietly(() => api.close(id));
        settleOrder();
        render();
        if (sheetOpenNow()) fillSheet();
        // 닫은 앱의 × 가 숨었다 — 포커스가 문서 밖으로 떨어졌으면 그 앱의 칸으로
        if (view() === 'launcher' && (!document.activeElement || document.activeElement === document.body)) focusTo(launcherFocusTarget(id));
    }
    function copyUrl(url) {
        if (!url || !navigator.clipboard) return;
        navigator.clipboard.writeText(url)
            .then(() => showNotification(bar.dataset.copied || '주소를 복사했습니다', 'success'))
            .catch(() => {});
    }
    function run(action) {
        const id = shown() || api.activeId();
        if (action === 'launcher') { toLauncher(); return; }
        if (action === 'reload') { api.reload(); return; }
        if (action === 'more') {
            if (menu.hidden) openMenu();
            else closeMenu(true);
            return;
        }
        closeMenu();
        if (action === 'back') api.back();
        else if (action === 'forward') api.forward();
        else if (action === 'external') {
            const url = id ? api.currentUrl(id) : '';
            if (url) window.open(url, '_blank', 'noopener');
        } else if (action === 'copy') copyUrl(id ? api.currentUrl(id) : '');
        else if (action === 'close' && id) closeApp(id);
    }

    // ── 처리기 ──
    container.addEventListener('click', (e) => {
        if (!isPhone()) return;
        const card = e.target.closest('#appLauncher .app-ntp-card[data-app-id]');
        if (card) { enter(card.dataset.appId); return; }
        const x = e.target.closest('[data-appm-close]');
        if (x) { closeApp(x.dataset.appmClose); return; }
        const pick = e.target.closest('[data-appm-open]');
        if (pick) {
            const id = pick.dataset.appmOpen;
            if (pick.closest('#appSwitchSheet')) {
                closeSheet(sheet);
                switchTo(id);
            } else {
                enter(id);
            }
            return;
        }
        const act = e.target.closest('[data-appm]');
        if (act && !act.disabled) run(act.dataset.appm);
    });

    container.addEventListener('apps:changed', () => {
        if (!isPhone() || busy) return;
        const active = api.activeId();
        if (view() === 'app') {
            if (!active) { toLauncher(); return; }
            // 브리지(iframe-navigate-to-app)가 다른 앱을 열었다 — 얇은 줄이 따라가고 셋 상한을 지킨다
            if (active !== container.dataset.mApp) { followApp(active); return; }
        } else if (active && api.openIds().length > PHONE_LIVE_MAX) {
            // 런처를 보는 중에 숨은 앱의 링크가 넷째 앱을 열었다 — 여기서도 셋 상한을 지킨다 (Minor 6)
            remember(active);
            enforceCap(active);
        }
        // 프레임 로드·브리지 보고처럼 이동만 있었으면 목록(격자 표시·시트)을 다시 만들지 않는다 — 다시 만들면
        // 그 안의 포커스가 body 로 떨어진다. 얇은 줄의 ←/→ 만 맞춘다 (Minor 2).
        if (sigOf() === lastSig) {
            if (view() === 'app') renderBar();
            return;
        }
        settleOrder();
        render();
        if (sheetOpenNow()) fillSheet();
    });

    // ⋯ 메뉴 안의 방향키 (role=menu, Minor 3): ↑↓ 는 돌아가며, Home·End 는 끝으로 — 비활성 항목은 건너뛴다
    menu.addEventListener('keydown', (e) => {
        const items = Array.from(menu.querySelectorAll('button:not([disabled])'));
        if (!items.length) return;
        const i = items.indexOf(document.activeElement);
        let next = -1;
        if (e.key === 'ArrowDown') next = (i + 1) % items.length;
        else if (e.key === 'ArrowUp') next = (i - 1 + items.length) % items.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = items.length - 1;
        if (next < 0) return;
        e.preventDefault();
        items[next].focus({ preventScroll: true });
    });

    window.addEventListener('popstate', (e) => {
        const prevSeq = seq;
        const st = e.state || {};
        seq = st.appsSeq || 0;
        if (!isPhone()) {
            // 데스크톱 폭: 폰 폭에서 쌓은 칸은 보이는 것을 바꾸지 않는다 — 같은 방향으로 계속 간다 (리뷰 I5)
            if (prevSeq || seq) {
                if (seq < prevSeq) history.back();
                else history.forward();
            }
            return;
        }
        closeMenu();
        if (sheetOpenNow()) closeSheet(sheet);
        // ‹ 가 스스로 걷어 낸 칸: 런처는 이미 보이고 있다 — 이것을 "헛도는 뒤로가기" 로 읽어 한 칸 더 가면
        // 앞의 칸(앱)으로 넘어가 버린다. 1초 안에 오지 않은 popstate 는 사용자의 것으로 본다.
        if (ownPopAt && Date.now() - ownPopAt < 1000) {
            ownPopAt = 0;
            return;
        }
        ownPopAt = 0;
        const d = popDecision({ state: st, prevSeq, openIds: api.openIds(), view: view() });
        if (d.skip) {
            if (d.do === 'back') history.back();
            else history.forward();
            return;
        }
        if (d.do === 'app') {
            showApp(d.id);
            settleFocus(nameBtn);
        } else {
            showLauncher();
            settleFocus(launcherFocusTarget(''));
        }
    });

    // 폭이 경계를 넘으면 모드가 바뀐다 (spec §1.4·§3): 시트·메뉴를 닫고, 데스크톱이면 폰 상태를 지운다 (R4: 앱은
    // 닫지 않는다). 다시 폰이 되면 지금 칸이 가리키는 앱을 보인다 — 이력과 화면이 어긋나지 않게 (리뷰 I5).
    PHONE.addEventListener('change', () => {
        closeMenu();
        if (sheetOpenNow()) closeSheet(sheet);
        if (isPhone()) {
            const st = history.state || {};
            if (st.appsView === 'app' && st.appsId && api.openIds().includes(st.appsId)) showApp(st.appsId);
            else showLauncher();
        } else {
            delete container.dataset.mView;
            delete container.dataset.mApp;
        }
    });

    registerSheetHooks('appSwitchSheet', { onOpen: fillSheet });

    // ── 시작: 새로고침한 앱 칸이면 그 앱, ?open= 은 그 앱의 전체 화면(쌓인 칸 없음 — ‹ 는 제자리에서 런처로),
    // 그 밖은 런처 ──
    if (!isPhone()) return;
    const frames = document.getElementById('appFrames');
    const wanted = frames ? frames.dataset.openApp : '';
    if (reloadedApp) {
        // 칸은 그대로 둔다: 뒤로가기는 그 아래의 런처 칸으로, ‹ 는 (mine 이 없으니) 제자리에서 런처로 바꾼다.
        showApp(reloadedApp);
    } else if (wanted && api.openIds().includes(wanted)) {
        showApp(wanted);
        // 이 칸을 앱 칸으로 표시한다 — ‹ 가 런처 칸을 쌓은 뒤의 뒤로가기가 이 앱을 다시 보인다 (리뷰 I4).
        // mine 은 비워 둔다: ‹ 는 이 칸을 걷어 내지 않고(걷어 내면 /apps 를 떠난다) 제자리에서 런처로 바꾼다.
        history.replaceState(Object.assign({}, history.state, { appsView: 'app', appsId: wanted, appsSeq: nextSeq() }), '', location.href);
    } else {
        showLauncher();
    }
}
